/**
 * Contracts repository — the data-access home for the `contracts` slice.
 *
 * The ONLY place that touches `contracts` data. Strangler-Fig per-slice
 * migration (manifest-6):
 *   Step 1 (done) — consolidated scattered `supabase.from("contracts")` here.
 *   Step 2 (done) — guarded NestJS endpoints (`apps/api/src/contracts/*`).
 *   Step 3 (HERE) — staff reads/writes flipped to the Nest client; the
 *                   owner/tenant self-service contract reads (which use OTHER
 *                   code paths, not this repo) stay on Supabase/RLS.
 *
 * ── Flip scope (owner-vs-staff authz split) ──────────────────────────────────
 *   FLIPPED to Nest (with a Supabase fallback): `list`, `getById`, `stats`,
 *     `createWithInvoices`, `updateWithInvoices`, `update` — all reached only by
 *     the staff dashboard (contract drawers/wizard, ContractDetails), whose
 *     callers hold `contracts.{view,create,edit}`.
 *   KEPT on Supabase (documented):
 *     - `listByTenant` — read by the staff TenantDetails page, whose route holds
 *        `tenants.view` (NOT `contracts.view`); a guarded `contracts` endpoint
 *        would 403 a tenants-only staffer. Same for `listByUnit` (UnitDetails →
 *        `units.view`). Owner/tenant *portal* contract reads use different code
 *        paths entirely (TenantContract, useOwnerData/useTenantData) — also Supabase.
 *     - `insert`, `generateContractNumber`, `getPropertyOwnerId`, `getUnitOwnerId`,
 *        `markUnitRented`, `markUnitAvailable` — sub-steps of the simple
 *        create/update flows that mutate the `properties`/`units` tables (other
 *        slices), orchestrated client-side.
 *
 * Three atomic RPCs are kept verbatim — they own contract creation, update,
 * and invoice generation in a single Postgres transaction. When flipped, Nest
 * forwards to the SAME RPCs (no double-entry re-home):
 *   - `generate_contract_number`      — deterministic serial per company.
 *   - `create_contract_with_invoices` — contract + payment-schedule insert.
 *   - `update_contract_with_invoices` — contract update + schedule rebuild.
 *
 * Each flipped method tries Nest first (only when an API is configured) and
 * falls back to the original Supabase query on any error — so this is a
 * reversible unit and local dev without the Nest API keeps working. RLS is
 * untouched. List/detail Nest responses are parsed through the shared
 * `contractSchema` (a too-strict DTO would silently fall back to Supabase — the
 * #1699 lesson).
 *
 * Convention audit (delete semantics):
 *   The `stats`/list queries filter `deleted_at IS NULL`, which means contracts
 *   use SOFT deletes. There is no hard `.delete()` here and this repository does
 *   not introduce one. Any future delete MUST be a soft delete:
 *     `.update({ deleted_at: new Date().toISOString() }).eq("id", id).eq("company_id", companyId)`
 *
 * Deferred (NOT migrated here): the cross-domain `supabase.from("contracts")`
 * reads in hooks/useOwnerData, hooks/useTenants, hooks/useFinance, pages/*, etc.
 * — see the deferred backlog (domain-debugging-matrix "read-by" column).
 */
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { z } from "zod";
import {
  contractSchema,
  nextContractNumberResponseSchema,
  contractDeleteSummarySchema,
  type ContractDeleteSummary,
} from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type { Database, Json } from "@/integrations/supabase/types";
import type { PaymentScheduleItem } from "@/types/contracts";
import { deriveContractTotalAmount } from "@/lib/contractHelpers";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";


// Authenticated Nest request bound to the "contracts" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
//
// UNSCOPED: no x-active-role forwarded/required. Used by writes (create,
// createWithInvoices, updateWithInvoices, update) — WRITE endpoints do NOT
// carry RowScopeGuard (capability-gated only, see contracts.controller.ts) —
// AND by the owner-portal read `listForOwnerPortal` (GET /contracts/owner,
// SupabaseAuthGuard only, no RowScopeGuard — an owner has no active-role
// concept to forward). Neither must ever be blocked or rerouted to the
// Supabase fallback by a missing/unresolved active role (issue #3278 — a
// client-side guard on a shared instance previously did exactly that).
const nestRequest = makeNestRequest("contracts");

// Row-scoped variant: the guarded read endpoints (GET /contracts, GET
// /contracts/:id, GET /contracts/stats) enforce RowScopeGuard, which requires
// the x-active-role header — without it the endpoints 403. The role is read
// from sessionStorage["dallali-active-role"] (set by the active-role selector)
// and forwarded on every request so the server-side row-scope predicate
// mirrors the live RLS the FE previously relied on; `requireActiveRole`
// short-circuits the boot-race window client-side (issue #3278).
const nestRequestScoped = makeNestRequest("contracts", {
  forwardActiveRole: true,
  requireActiveRole: true,
});

type ContractRow = Database["public"]["Tables"]["contracts"]["Row"];
type ContractInsert = Database["public"]["Tables"]["contracts"]["Insert"];
type ContractUpdate = Database["public"]["Tables"]["contracts"]["Update"];
type ContractType = Database["public"]["Enums"]["contract_type"];
type ContractStatus = Database["public"]["Enums"]["contract_status"];

// ─── Public data shapes ──────────────────────────────────────────────────────

export interface ContractListParams {
  companyId: string;
  /** Non-null = employee scoped to these property ids; null = no assignment filter (admin). */
  assignedPropertyIds: string[] | null;
}

/**
 * Owner-portal contract row (S9) — full contract row + the NARROW relations the
 * owner portal renders (property label, unit number, tenant display name).
 * Matches both the Nest `GET /contracts/owner` wire shape and the Supabase
 * fallback embed byte-for-byte.
 */
export type OwnerPortalContract = ContractRow & {
  property: { name: string; name_en: string | null } | null;
  unit: { unit_number: string | null } | null;
  tenant: {
    first_name: string | null;
    last_name: string | null;
    first_name_en: string | null;
    last_name_en: string | null;
  } | null;
};

export interface CreateContractData {
  property_id: string;
  unit_id: string;
  contract_type?: ContractType;
  start_date: string;
  end_date: string;
  rent_amount?: number;
  total_amount?: number;
  deposit_amount?: number;
  payment_frequency?: string;
  payments_count?: number;
  tenant_id?: string;
  owner_id?: string;
  ejar_number?: string;
  terms?: string;
  notes?: string;
  contract_fee?: number;
  contract_fee_payer?: string;
  management_fee?: number;
  management_fee_payer?: string;
  brokerage_fee?: number;
  brokerage_fee_payer?: string;
  brokerage_fee_type?: string;
  brokerage_fee_value?: string;
  marketing_fee?: number;
  marketing_fee_payer?: string;
  marketing_fee_type?: string;
  marketing_fee_value?: string;
  contract_management_fee?: number;
  contract_management_fee_payer?: string;
  contract_management_fee_type?: string;
  contract_management_fee_value?: string;
  property_management_fee?: number;
  property_management_fee_payer?: string;
  property_management_fee_type?: string;
  property_management_fee_value?: string;
}

export interface ContractUnitServiceEntry {
  amount: number;
  payer: "tenant" | "owner";
  type?: "meter" | "fixed" | "included" | "not_available";
}

export interface ContractUnitPayload {
  unit_id: string;
  rent_amount: number;
  service_fees: Partial<Record<
    | "sewage" | "maintenance" | "cleaning" | "security"
    | "electricity" | "water" | "gas" | "internet",
    ContractUnitServiceEntry
  >>;
}

export interface CreateContractWithInvoicesData extends CreateContractData {
  paymentSchedule: PaymentScheduleItem[];
  units?: ContractUnitPayload[];
}

export interface UpdateContractWithInvoicesData
  extends Partial<Omit<CreateContractData, "status">> {
  id: string;
  status?: ContractStatus;
  paymentSchedule?: PaymentScheduleItem[];
  units?: ContractUnitPayload[];
}

export interface ContractsStats {
  total: number;
  active: number;
  expired: number;
  rent: number;
  sale: number;
}

/** Narrow active-contract row for the task property-context sidebar (fe-tail T9). */
export interface TaskContextContractRow {
  id: string;
  rent_amount: number | null;
  start_date: string;
  end_date: string;
  status: string;
  tenant_id: string | null;
}

// ─── Repository ──────────────────────────────────────────────────────────────

// ── Supabase implementations (fallback for the flipped staff methods, and the
//    live path for the methods kept on Supabase) ───────────────────────────────

async function listViaSupabase({ companyId, assignedPropertyIds }: ContractListParams) {
  let query = supabase
    .from("contracts")
    .select(`
      *,
      property:properties!contracts_property_id_fkey(name, name_en, purpose, owner:owners(id, full_name, full_name_en)),
      unit:units!contracts_unit_id_fkey(unit_number, purpose, owner:owners(id, full_name, full_name_en)),
      tenant:tenants!contracts_tenant_id_fkey(
        id,
        tenant_type,
        first_name,
        last_name,
        first_name_en,
        last_name_en,
        commercial_name,
        commercial_name_en,
        phone,
        id_number
      ),
      owner:owners(id, full_name, full_name_en)
    `)
    .eq("company_id", companyId)
    .is("deleted_at", null);

  if (assignedPropertyIds && assignedPropertyIds.length > 0) {
    query = query.in("property_id", assignedPropertyIds);
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching contracts:", serializeError(error));
    throw error;
  }

  return data;
}

async function getByIdViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from("contracts")
    .select(`
      *,
      property:properties!contracts_property_id_fkey(id, name, name_en, address, city, district, property_type),
      unit:units!contracts_unit_id_fkey(id, unit_number, unit_type, area, floor_number),
      tenant:tenants!contracts_tenant_id_fkey(
        id,
        tenant_type,
        first_name,
        last_name,
        first_name_en,
        last_name_en,
        commercial_name,
        commercial_name_en,
        phone,
        email,
        id_number
      ),
      owner:owners(id, full_name, full_name_en, phone, email)
    `)
    .eq("id", id)
    .eq("company_id", companyId)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) {
    console.error("Error fetching contract:", serializeError(error));
    throw error;
  }

  return data;
}

// The Supabase queries above carry the precise relation typing the dashboard
// consumes. The Nest path returns the SAME payload (Nest mirrors these exact
// `.select()` strings), validated at runtime by the shared `contractSchema`;
// we cast the parsed rows back to the Supabase-inferred type so consumers see
// no type drift across the flip.
type ContractListResult = Awaited<ReturnType<typeof listViaSupabase>>;
type ContractDetailResult = Awaited<ReturnType<typeof getByIdViaSupabase>>;

// Stats projection — loose row schema for the Nest path (status/contract_type
// stay open strings so a new enum value never rejects the response); cast back
// to the Supabase-inferred projection so `useContractsStats` sees no drift.
async function statsViaSupabase(companyId: string, range?: { startDate?: string; endDate?: string }) {
  let query = supabase
    .from("contracts")
    .select("status, contract_type, end_date")
    .eq("company_id", companyId)
    .is("deleted_at", null);
  if (range?.startDate) query = query.gte("created_at", range.startDate);
  if (range?.endDate) query = query.lte("created_at", range.endDate);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}
type ContractStatsResult = Awaited<ReturnType<typeof statsViaSupabase>>;
const statsRowSchema = z.object({
  status: z.string(),
  contract_type: z.string(),
  end_date: z.string(),
});

export const contractsRepository = {
  /**
   * Canonical contracts list with full relations. Always company-scoped,
   * excludes soft-deleted rows. When `assignedPropertyIds` is a non-empty
   * array the result is restricted to those properties (employee scope).
   *
   * Staff op — flipped to Nest (company + employee-scope resolved server-side
   * from the JWT), Supabase fallback. Note: the Nest list is company-wide; the
   * employee `assignedPropertyIds` narrowing is currently only applied on the
   * Supabase path, so employee-scoped callers fall through to Supabase below.
   */
  async list(params: ContractListParams) {
    const { companyId, assignedPropertyIds } = params;
    // Row-scope is now enforced server-side via RowScopeGuard + x-active-role header
    // (forwarded by nestRequestScoped above). The Nest endpoint
    // applies the CONTRACTS_SCOPE_MAPPING predicate (owner_id / property_id scope)
    // identically to the live RLS, so employee-scoped lists no longer need the
    // client-side assignedPropertyIds filter on the Supabase path.
    if (API_URL) {
      try {
        const rows = await nestRequestScoped<unknown[]>(API_ROUTES.contracts.list());
        return z.array(contractSchema).parse(rows) as unknown as ContractListResult;
      } catch (err) {
        reportNestFallback("contracts.list", err);
      }
    }
    return listViaSupabase({ companyId, assignedPropertyIds });
  },

  /**
   * Owner-portal contract list (S9) — the authenticated owner's OWN contracts,
   * read-only. Nest-first via the auth-only `GET /contracts/owner` (the server
   * resolves the owner PERSON-WIDE: every owners row matching the caller's
   * user_id or shared national_id — the fragmented-person fix). Supabase
   * fallback keeps the previous single-owner-row read (`owner_id = ownerId`) —
   * an acceptable degradation on the rare fallback path: it shows the caller's
   * primary owner row's contracts only, exactly what shipped before S9.
   */
  async listForOwnerPortal(
    ownerId: string,
    companyId: string,
  ): Promise<OwnerPortalContract[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.contracts.owner());
        return z.array(contractSchema).parse(rows) as unknown as OwnerPortalContract[];
      } catch (err) {
        reportNestFallback("contracts.listForOwnerPortal", err);
      }
    }

    const { data, error } = await supabase
      .from("contracts")
      .select(`
        *,
        property:properties(name, name_en),
        unit:units(unit_number),
        tenant:tenants(first_name, last_name, first_name_en, last_name_en)
      `)
      .eq("owner_id", ownerId)
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching owner portal contracts:", serializeError(error));
      throw error;
    }
    return (data ?? []) as unknown as OwnerPortalContract[];
  },

  /**
   * Single contract by id with detailed relations. Company-scoped + excludes
   * soft-deleted rows. Staff op — flipped to Nest, Supabase fallback.
   */
  async getById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequestScoped<unknown>(API_ROUTES.contracts.getById(id));
        return (row ? contractSchema.parse(row) : null) as unknown as ContractDetailResult;
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("contracts.getById", err);
      }
    }
    return getByIdViaSupabase(id, companyId);
  },

  /**
   * Look up the owner_id for a property (used during contract create to
   * auto-populate owner_id). Company-scoped.
   */
  async getPropertyOwnerId(propertyId: string, companyId: string): Promise<string | null | undefined> {
    const { data } = await supabase
      .from("properties")
      .select("owner_id")
      .eq("id", propertyId)
      .eq("company_id", companyId)
      .maybeSingle();
    return data?.owner_id;
  },

  /**
   * Look up the owner_id for a unit (fallback for multi-owner properties where
   * property.owner_id is null). Company-scoped.
   */
  async getUnitOwnerId(unitId: string, companyId: string): Promise<string | null | undefined> {
    const { data } = await supabase
      .from("units")
      .select("owner_id")
      .eq("id", unitId)
      .eq("company_id", companyId)
      .maybeSingle();
    return data?.owner_id;
  },

  /**
   * Generate the next contract number for a company — Nest-first. The guarded
   * endpoint (`GET /contracts/next-number`, `contracts.create`) wraps the
   * `generate_contract_number` RPC and derives the company from the JWT, so no
   * client-supplied id is trusted. Falls back to the RPC directly on any Nest
   * error. `companyId` is only used by the Supabase fallback path. The
   * fallback rethrows on an RPC error (rather than swallowing to `null`) so a
   * transient DB failure can never silently persist a contract with no
   * number — matches `deleteCascade`'s fail-loud fallback below.
   */
  async generateContractNumber(companyId: string): Promise<string | null> {
    if (API_URL) {
      try {
        const res = nextContractNumberResponseSchema.parse(
          await nestRequest<unknown>(API_ROUTES.contracts.nextNumber(), { method: "GET" }),
        );
        return res.contract_number ?? null;
      } catch (err) {
        reportNestFallback("contracts.generateContractNumber", err);
      }
    }
    const { data, error } = await supabase
      .rpc("generate_contract_number", { company_uuid: companyId });
    if (error) throw error;
    return data ?? null;
  },

  /**
   * Cascade soft-delete a contract — Nest-first. The guarded endpoint
   * (`DELETE /contracts/:id`, `contracts.delete`) wraps the
   * `delete_contract_cascade` RPC: company scope is derived server-side (404
   * on cross-tenant), the delete is audited (Fortress rule 2), and property/
   * unit card caches invalidate inside the write path. Falls back to the RPC
   * directly on any Nest error. NOTE: the caller must still run
   * `revertUnitsForTerminatedContract` BEFORE this — freeing the lease's units
   * is a distinct auth.uid()-scoped op outside this wrap. Returns the RPC's
   * jsonb count summary, validated against the shared `contractDeleteSummarySchema`
   * (single source of truth for the wire shape with `get_contract_delete_summary`).
   */
  async deleteCascade(contractId: string, companyId: string): Promise<ContractDeleteSummary> {
    if (API_URL) {
      try {
        return contractDeleteSummarySchema.parse(
          await nestRequest<unknown>(API_ROUTES.contracts.remove(contractId), {
            method: "DELETE",
          }),
        );
      } catch (err) {
        reportNestFallback("contracts.deleteCascade", err);
      }
    }
    const { data, error } = await supabase.rpc("delete_contract_cascade", {
      p_contract_id: contractId,
      p_company_id: companyId,
    });
    if (error) throw error;
    return contractDeleteSummarySchema.parse(data);
  },

  /**
   * Pre-delete impact summary (`get_contract_delete_summary` RPC — the
   * jsonb counts the delete-confirm dialog renders). RLS-scoped like the
   * pre-consolidation site; throws on error (the caller falls back to the
   * dialog without counts). Result passed through unparsed — the caller
   * casts to its local counts shape, exactly as before.
   */
  async getDeleteSummary(contractId: string): Promise<unknown> {
    const { data, error } = await supabase.rpc("get_contract_delete_summary", {
      p_contract_id: contractId,
    });
    if (error) throw error;
    return data;
  },

  /**
   * Stamp the Ejar verification fields after a verified PDF upload (both the
   * silent-match and the confirmed-link paths — `ejar_number` present only on
   * the latter). Scoped by id AND company_id (the explicit company filter was
   * added at consolidation per the repository convention — a no-op under RLS,
   * which already restricted the update to the caller's company).
   */
  async setEjarVerification(
    contractId: string,
    companyId: string,
    patch: {
      ejar_number?: string;
      ejar_document_url: string;
      ejar_verified: boolean;
      ejar_verified_at: string;
    },
  ): Promise<void> {
    const { error } = await supabase
      .from("contracts")
      .update(patch)
      .eq("id", contractId)
      .eq("company_id", companyId);
    if (error) throw error;
  },

  /**
   * Simple create (no invoice generation) — Nest-first. The guarded endpoint
   * owns BOTH the insert and the unit→rented side effect for non-draft
   * contracts, so the property-card / units_stats caches invalidate inside
   * the write path (cache-layer round-8). The Supabase fallback reproduces
   * the same pair client-side (where no cache exists to go stale).
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the
   * Supabase fallback only runs when Nest verifiably did not commit (safe
   * 4xx / unconfigured). An ambiguous outcome (network, timeout, 5xx, parse
   * failure after a 2xx) RETHROWS instead of re-issuing the insert — a
   * fallback after a committed-but-lost Nest write would double-create the
   * contract.
   */
  async create(insertData: ContractInsert) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.contracts.create(), {
          method: "POST",
          body: JSON.stringify(insertData),
        });
        return contractSchema.parse(row) as unknown as Awaited<
          ReturnType<typeof contractsRepository.insert>
        >;
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("contracts.create", err);
      }
    }
    const data = await contractsRepository.insert(insertData);
    if (data.status !== "draft" && insertData.unit_id) {
      await contractsRepository.markUnitRented(insertData.unit_id, insertData.company_id as string);
    }
    return data;
  },

  /**
   * Insert a contract row (the simple create path — no invoice generation).
   * Company-scoped. Returns the inserted row.
   */
  async insert(insertData: ContractInsert) {
    const { data, error } = await supabase
      .from("contracts")
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error("Error creating contract:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * Sync unit status to 'rented' after a contract is created / activated.
   * Not company-scoped on units intentionally — the unit_id already came from
   * a company-scoped contract row. Side-effect only; errors are silent.
   */
  async markUnitRented(unitId: string, companyId: string) {
    await supabase
      .from("units")
      .update({ status: "rented" as const })
      .eq("id", unitId)
      .eq("company_id", companyId);
  },

  /**
   * Sync unit status to 'available' after a contract is terminated / expired,
   * provided no other active contracts exist on the same unit.
   */
  async markUnitAvailable(unitId: string, companyId: string) {
    await supabase
      .from("units")
      .update({ status: "available" as const })
      .eq("id", unitId)
      .eq("company_id", companyId);
  },

  /**
   * Revert EVERY unit on a terminated/expired contract to 'available' — the
   * primary `unit_id` AND all multi-unit `contract_units` rows — via the
   * `revert_units_for_terminated_contract` RPC (Epic #3034 / C3). Each unit is
   * reverted only when no OTHER active contract still holds it (checked across
   * both `contracts.unit_id` and other contracts' `contract_units`, which the
   * per-unit client query could not see). Atomic + company-scoped server-side.
   * Returns the number of units reverted. Side-effect only.
   *
   * Flipped to Nest (`POST /contracts/:id/revert-units`, `contracts.edit` +
   * audited; the endpoint forwards the caller's identity so the RPC's internal
   * `auth.uid()` membership guard fires), Supabase fallback. `companyId` is
   * only used by the fallback — Nest derives it from the JWT.
   */
  async revertUnitsForTerminatedContract(contractId: string, companyId: string): Promise<number> {
    if (API_URL) {
      try {
        const res = await nestRequest<{ reverted?: number }>(
          API_ROUTES.contracts.revertUnits(contractId),
          { method: "POST" },
        );
        return Number(res?.reverted ?? 0);
      } catch (err) {
        // Reviewed 2026-07-25 (write-guard sweep): read-path fallback is SAFE
        // here. `revert_units_for_terminated_contract` is idempotent
        // server-side (a second run finds the units already available and
        // reverts 0), so retrying the same RPC after an ambiguous Nest
        // failure cannot over-revert — mirrors invoices.payMasterInvoice.
        reportNestFallback("contracts.revertUnitsForTerminatedContract", err);
      }
    }
    const { data, error } = await supabase.rpc("revert_units_for_terminated_contract", {
      p_contract_id: contractId,
      p_company_id: companyId,
    });
    if (error) throw error;
    return (data as number) ?? 0;
  },

  /**
   * Atomic contract + invoice creation via the `create_contract_with_invoices`
   * RPC. The RPC owns: contract insert, payment-schedule invoice fan-out, fee
   * invoices, and multi-unit contract_units rows. Do NOT replace with separate
   * from().insert() calls — that would break the Postgres transaction.
   *
   * Staff op — flipped to Nest (which forwards to the SAME RPC server-side),
   * Supabase fallback. The client-resolved `ownerId` is forwarded as `owner_id`
   * so Nest uses it directly (its own property/unit owner resolution only runs
   * when owner_id is null). The RPC result shape varies, so it is passed through
   * unparsed.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the
   * Supabase fallback only runs when Nest verifiably did not commit (safe
   * 4xx / unconfigured); an ambiguous outcome (network, timeout, 5xx, parse
   * failure after a 2xx) RETHROWS instead of re-issuing the call. This one is
   * especially sharp — `create_contract_with_invoices` has NO idempotency
   * guard of its own, so a wrongful re-issue after an already-committed Nest
   * write doesn't just double a row: it creates a SECOND full contract plus
   * its entire generated invoice set.
   */
  async createWithInvoices(companyId: string, contractData: CreateContractWithInvoicesData, ownerId: string | null) {
    // ── Fortress Rule 1 — amounts are derived, never received ──────────────────
    // The persisted contract header `total_amount` is RECOMPUTED here from the
    // server-trustworthy source rows (the full-term rent the payment schedule
    // sums to + the tenant-paid service fees the rent invoices fold in), so it
    // equals Σ(rent-invoice subtotals) exactly. The client-supplied
    // `contractData.total_amount` is intentionally IGNORED — a tampered or stale
    // value can never reach the database (AC4: commercial service fees were
    // "saved but not counted" in the header total). VAT stays out of the header
    // total (ex-VAT contract value); invoices carry rent-only VAT as today.
    const derivedTotalAmount = deriveContractTotalAmount(
      contractData.paymentSchedule.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
      contractData.units,
      contractData.paymentSchedule.length,
    );
    // Schedule-LESS path (e.g. a sale contract with no rent schedule): the
    // derived total is 0, so the agreed contract amount is used. Unlike rent —
    // which is fully derivable from rent×term+services — a sale's agreed price is
    // a PRIMARY negotiated input: it is not recomputable from other persisted
    // data, and it deliberately need NOT equal the unit/listing price (the parties
    // negotiate). We therefore accept it, but still fail-closed per Fortress R1:
    // only a finite, NON-NEGATIVE number may persist; a tampered/NaN/negative
    // client value is rejected to undefined rather than trusted. (Whenever a
    // payment schedule IS present, the schedule-derived total above always wins,
    // so the client value can never override a schedule-backed contract.)
    const clientTotal = Number(contractData.total_amount);
    const sanitizedClientTotal =
      Number.isFinite(clientTotal) && clientTotal >= 0 ? clientTotal : undefined;
    // SALE down-payment deduction (spec item 2, Fortress R1): the payment
    // schedule bills the BALANCE (sale price − down payment), so the
    // schedule-derived total above is the balance. The persisted header total
    // must be the FULL sale price, so we add the down payment (deposit_amount)
    // back — deriving the header from trustworthy source rows (schedule + down
    // payment), never from the client's total field. Rent/sublease unchanged.
    const isSaleContract = contractData.contract_type === "sale";
    const saleDownPayment = isSaleContract ? Number(contractData.deposit_amount) || 0 : 0;
    const persistedTotalAmount =
      derivedTotalAmount > 0
        ? isSaleContract
          ? Math.round((derivedTotalAmount + saleDownPayment) * 100) / 100
          : derivedTotalAmount
        : sanitizedClientTotal || undefined;

    if (API_URL) {
      try {
        return await nestRequest<unknown>(API_ROUTES.contracts.createWithInvoices(), {
          method: "POST",
          // Override the client total with the server-derived value (Fortress R1).
          body: JSON.stringify({
            ...contractData,
            total_amount: persistedTotalAmount,
            // Empty-string optional UUIDs must be ABSENT, mirroring the Supabase
            // fallback below — the wizard seeds tenantId: "", and "" fails the
            // Nest DTO's @IsUUID → 400 → silent Supabase fallback on every
            // tenant-less create (#3461).
            tenant_id: contractData.tenant_id || undefined,
            owner_id: ownerId || undefined,
          }),
        });
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("contracts.createWithInvoices", err);
      }
    }
    const { data, error } = await supabase.rpc("create_contract_with_invoices", {
      p_company_id: companyId,
      p_property_id: contractData.property_id,
      p_unit_id: contractData.unit_id,
      p_tenant_id: contractData.tenant_id || undefined,
      p_owner_id: ownerId || undefined,
      p_contract_type: contractData.contract_type || "rent",
      p_start_date: contractData.start_date,
      p_end_date: contractData.end_date,
      p_rent_amount: contractData.rent_amount || undefined,
      p_total_amount: persistedTotalAmount,
      p_deposit_amount: contractData.deposit_amount || 0,
      p_payments_count: contractData.payments_count || undefined,
      p_payment_frequency: contractData.payment_frequency || undefined,
      p_ejar_number: contractData.ejar_number || undefined,
      p_notes: contractData.notes || undefined,
      p_terms: contractData.terms || undefined,
      p_payment_schedule: contractData.paymentSchedule.map((p) => ({
        paymentNumber: p.paymentNumber,
        dueDate: p.dueDate,
        amount: p.amount,
      })),
      p_contract_fee: contractData.contract_fee || 0,
      p_contract_fee_payer: contractData.contract_fee_payer || "tenant",
      p_management_fee: contractData.management_fee || 0,
      p_management_fee_payer: contractData.management_fee_payer || "owner",
      p_brokerage_fee: contractData.brokerage_fee || 0,
      p_brokerage_fee_payer: contractData.brokerage_fee_payer || "tenant",
      p_marketing_fee: contractData.marketing_fee || 0,
      p_marketing_fee_payer: contractData.marketing_fee_payer || "tenant",
      p_contract_management_fee: contractData.contract_management_fee || 0,
      p_contract_management_fee_payer: contractData.contract_management_fee_payer || "tenant",
      p_property_management_fee: contractData.property_management_fee || 0,
      p_property_management_fee_payer: contractData.property_management_fee_payer || "owner",
      p_units:
        contractData.units && contractData.units.length > 0
          ? (contractData.units as unknown as Json)
          : null,
    });

    if (error) {
      console.error("Error creating contract with invoices:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * Atomic contract update + invoice schedule rebuild via the
   * `update_contract_with_invoices` RPC. Omitting `p_payment_schedule` (null)
   * leaves existing invoices intact. Fee params use COALESCE on the server:
   * null → keep existing; 0 → clear the column.
   */
  async updateWithInvoices(
    id: string,
    companyId: string,
    params: Omit<UpdateContractWithInvoicesData, "id">,
  ) {
    if (API_URL) {
      try {
        return (await nestRequest<unknown>(API_ROUTES.contracts.updateWithInvoices(id), {
          method: "PUT",
          body: JSON.stringify(params),
        })) as ContractRow;
      } catch (err) {
        reportNestFallback("contracts.updateWithInvoices", err);
      }
    }
    // The fee params (p_contract_fee, p_brokerage_fee, …) and p_units were added
    // in migrations 20260514150000 / 20260517120000 and types.ts hasn't been
    // regenerated to include them yet, so the rpc signature needs an `any` cast.
    // `.bind(supabase)` is REQUIRED: extracting `supabase.rpc` into a standalone
    // variable detaches it from the client, so the call runs with `this===undefined`
    // and throws "Cannot read properties of undefined (reading 'rest')" inside
    // supabase-js (`return this.rest.rpc(...)`). The createWithInvoices path above
    // calls supabase.rpc(...) directly, which keeps `this` bound for free.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rpcFn = (supabase.rpc as any).bind(supabase) as (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
    const { data, error } = await rpcFn("update_contract_with_invoices", {
      p_contract_id: id,
      p_company_id: companyId,
      p_property_id: params.property_id ?? null,
      p_unit_id: params.unit_id ?? null,
      p_tenant_id: params.tenant_id ?? null,
      p_owner_id: params.owner_id ?? null,
      p_contract_type: params.contract_type ?? null,
      p_start_date: params.start_date ?? null,
      p_end_date: params.end_date ?? null,
      p_rent_amount: params.rent_amount ?? null,
      p_total_amount: params.total_amount ?? null,
      p_deposit_amount: params.deposit_amount ?? null,
      p_payments_count: params.payments_count ?? null,
      p_payment_frequency: params.payment_frequency ?? null,
      p_ejar_number: params.ejar_number ?? null,
      p_notes: params.notes ?? null,
      p_terms: params.terms ?? null,
      p_payment_schedule: params.paymentSchedule
        ? params.paymentSchedule.map((p) => ({
            paymentNumber: p.paymentNumber,
            dueDate: p.dueDate,
            amount: p.amount,
          }))
        : null,
      p_contract_fee: params.contract_fee ?? null,
      p_contract_fee_payer: params.contract_fee_payer ?? null,
      p_management_fee: params.management_fee ?? null,
      p_management_fee_payer: params.management_fee_payer ?? null,
      p_brokerage_fee: params.brokerage_fee ?? null,
      p_brokerage_fee_payer: params.brokerage_fee_payer ?? null,
      p_marketing_fee: params.marketing_fee ?? null,
      p_marketing_fee_payer: params.marketing_fee_payer ?? null,
      p_contract_management_fee: params.contract_management_fee ?? null,
      p_contract_management_fee_payer: params.contract_management_fee_payer ?? null,
      p_property_management_fee: params.property_management_fee ?? null,
      p_property_management_fee_payer: params.property_management_fee_payer ?? null,
      p_units: params.units && params.units.length > 0 ? params.units : null,
    });

    if (error) {
      console.error("Error updating contract with invoices:", serializeError(error as { message?: string }));
      throw error;
    }

    return data as ContractRow;
  },

  /**
   * Simple contract field update (status change, notes, etc.). Company-scoped.
   * Returns the updated row or null if not found.
   *
   * Staff op — flipped to Nest (PUT), Supabase fallback. The Nest response is
   * the updated `*` row, validated through the shared `contractSchema`.
   */
  async update(id: string, companyId: string, updateData: ContractUpdate) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.contracts.update(id), {
          method: "PUT",
          body: JSON.stringify(updateData),
        });
        // When Nest returns a non-null row, parse and return it (#2261 fix).
        // A null/undefined response means the update returned no row (which should
        // not happen on a successful PUT) — fall through to the Supabase path so
        // the caller gets a result rather than null, which would cause a "Contract
        // not found or access denied" error in useUpdateContract.
        if (row) return contractSchema.parse(row) as unknown as ContractRow;
        reportNestFallback("contracts.update", new Error("Nest returned empty response body"));
      } catch (err) {
        reportNestFallback("contracts.update", err);
      }
    }
    const { data, error } = await supabase
      .from("contracts")
      .update(updateData)
      .eq("id", id)
      .eq("company_id", companyId)
      .select()
      .maybeSingle();

    if (error) {
      console.error("Error updating contract:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * Propagate a property's new owner to its DRAFT/PENDING contracts.
   *
   * When a property's owner is swapped, draft/pending contracts kept the stale
   * `owner_id` FK (set at creation), so other units on the property showed the
   * OLD owner until a manual delete-and-recreate. This updates them in place.
   * Finalized (active/terminated/expired) contracts keep their historical owner
   * snapshot — only mutable pre-final contracts are touched, and soft-deleted
   * rows are skipped. Company-scoped for multi-tenant isolation. Stays on
   * Supabase (a narrow cross-domain write orchestrated from the properties flow,
   * like the unit/property sub-steps above).
   */
  async propagateDraftContractOwner(
    propertyId: string,
    newOwnerId: string,
    companyId: string,
  ): Promise<void> {
    const patch: ContractUpdate = { owner_id: newOwnerId };
    const { error } = await supabase
      .from("contracts")
      .update(patch)
      .eq("property_id", propertyId)
      .eq("company_id", companyId)
      .in("status", ["draft", "pending"])
      .is("deleted_at", null);
    if (error) {
      console.error("Error propagating draft contract owner:", serializeError(error));
      throw error;
    }
  },

  /**
   * Tenant-portal contracts list (used by useTenantContracts) — same shape as
   * the pre-existing inline query it replaces (unit/property id+name joins,
   * NOT company-scoped — the tenant identifies contracts by their own
   * tenant_id only). Optional `range` bounds `created_at` (month/year time
   * filter, CEO ask — "created within the selected month", matching the
   * admin dashboard's usePropertiesStats/useContractsStats convention).
   *
   * Distinct from `listByTenant` below (a company-scoped staff-facing variant
   * with a slightly different select) — kept separate to avoid changing
   * either call site's existing shape.
   */
  async listForTenantPortal(
    tenantId: string,
    range?: { startDate?: string; endDate?: string },
  ) {
    let query = supabase
      .from("contracts")
      .select(`
        *,
        unit:units(id, unit_number),
        property:properties(id, name, name_en)
      `)
      .eq("tenant_id", tenantId)
      .is("deleted_at", null);

    if (range?.startDate) query = query.gte("created_at", range.startDate);
    if (range?.endDate) query = query.lte("created_at", range.endDate);

    const { data, error } = await query.order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Contracts by tenant — lightweight list with property/unit names.
   * Company-scoped + soft-delete filter.
   */
  async listByTenant(tenantId: string, companyId: string) {
    const { data, error } = await supabase
      .from("contracts")
      .select(`
        *,
        property:properties!contracts_property_id_fkey(name, name_en),
        unit:units!contracts_unit_id_fkey(unit_number)
      `)
      .eq("company_id", companyId)
      .eq("tenant_id", tenantId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching tenant contracts:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * Contracts by unit — lightweight list with property/tenant/owner names.
   * Company-scoped + soft-delete filter.
   */
  async listByUnit(unitId: string, companyId: string) {
    const { data, error } = await supabase
      .from("contracts")
      .select(`
        *,
        property:properties!contracts_property_id_fkey(name, name_en),
        tenant:tenants!contracts_tenant_id_fkey(
          id,
          tenant_type,
          first_name,
          last_name,
          first_name_en,
          last_name_en,
          commercial_name,
          commercial_name_en,
          phone
        ),
        owner:owners(id, full_name, full_name_en)
      `)
      .eq("company_id", companyId)
      .eq("unit_id", unitId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching unit contracts:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * Minimal stats projection: status + type + end_date columns only.
   * The hook applies `getEffectiveStatus()` in-memory to compute the
   * accurate active/expired counts. Company-scoped + soft-delete filter.
   *
   * Convention: uses `.is("deleted_at", null)` — confirms SOFT delete semantics.
   *
   * Staff op — flipped to Nest (`/contracts/stats`), Supabase fallback. The Nest
   * response is the same minimal projection; parsed loosely (passthrough) so a
   * column-set match isn't required to avoid silent fallback.
   */
  async stats(companyId: string, range?: { startDate?: string; endDate?: string }) {
    if (API_URL) {
      try {
        const rows = await nestRequestScoped<unknown[]>(
          API_ROUTES.contracts.stats(range?.startDate, range?.endDate),
        );
        return statsRowSchema.array().parse(rows) as unknown as ContractStatsResult;
      } catch (err) {
        reportNestFallback("contracts.stats", err);
      }
    }
    return statsViaSupabase(companyId, range);
  },

  /**
   * Sale-contract totals for the finance stats "total sales" figure.
   * Company-scoped + soft-delete filter. KEPT on Supabase — batched finance
   * read. NEVER throws: the pre-consolidation site destructured `{ data }`
   * only (a failed read shows total sales as 0), so an error resolves to [].
   */
  async listSaleTotals(
    companyId: string,
  ): Promise<{ total_amount: number | null; contract_type: string | null }[]> {
    const { data } = await supabase
      .from("contracts")
      .select("total_amount, contract_type")
      .eq("company_id", companyId)
      .eq("contract_type", "sale")
      .is("deleted_at", null);
    return data ?? [];
  },

  /**
   * Per-contract fee columns for the finance summary cards, optionally
   * date-windowed on created_at. Company-scoped + soft-delete filter. KEPT on
   * Supabase — batched finance read. Throws on error (the pre-consolidation
   * site checked and rethrew the error).
   */
  async listFeeTotals(
    companyId: string,
    filter?: { startDate?: string; endDate?: string },
  ): Promise<
    {
      id: string;
      contract_type: string | null;
      created_at: string;
      marketing_fee: number | null;
      contract_management_fee: number | null;
      brokerage_fee: number | null;
      management_fee: number | null;
      property_management_fee: number | null;
    }[]
  > {
    let query = supabase
      .from("contracts")
      .select("id, contract_type, created_at, marketing_fee, contract_management_fee, brokerage_fee, management_fee, property_management_fee")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (filter?.startDate) {
      query = query.gte("created_at", filter.startDate);
    }

    if (filter?.endDate) {
      query = query.lte("created_at", filter.endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Contract date-windows for the occupancy chart (active + expired, incl.
   * soft-delete filter). Company-scoped. KEPT on Supabase — batched finance
   * read. NEVER throws: the pre-consolidation site destructured `{ data }`
   * only (a failed read charts 0% occupancy), so an error resolves to [].
   */
  async listOccupancyWindows(
    companyId: string,
  ): Promise<
    {
      start_date: string;
      end_date: string;
      unit_id: string | null;
      status: string;
    }[]
  > {
    const { data } = await supabase
      .from("contracts")
      .select("start_date, end_date, unit_id, status")
      .eq("company_id", companyId)
      .in("status", ["active", "expired"])
      .is("deleted_at", null);
    return data ?? [];
  },

  /**
   * A property's contracts with property/unit/tenant context, newest first
   * (the property-drawer Contracts tab). Company-scoped + soft-delete filter.
   * KEPT on Supabase — property-drawer aggregate read.
   */
  async listByProperty(companyId: string, propertyId: string) {
    const { data, error } = await supabase
      .from("contracts")
      .select(`
          *,
          property:properties(name, name_en),
          unit:units(id, unit_number, unit_type),
          tenant:tenants!contracts_tenant_id_fkey(
            id,
            tenant_type,
            first_name,
            last_name,
            first_name_en,
            last_name_en,
            commercial_name,
            commercial_name_en,
            phone
          )
        `)
      .eq("company_id", companyId)
      .eq("property_id", propertyId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * `{ tenant_id }` rows for a property's contracts (non-null tenants only) —
   * step 1 of the property-drawer Tenants tab. Company-scoped + soft-delete
   * filter. KEPT on Supabase.
   */
  async listTenantIdRowsByProperty(
    companyId: string,
    propertyId: string,
  ): Promise<{ tenant_id: string | null }[]> {
    const { data, error } = await supabase
      .from("contracts")
      .select("tenant_id")
      .eq("company_id", companyId)
      .eq("property_id", propertyId)
      .is("deleted_at", null)
      .not("tenant_id", "is", null);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Full contract history (with tenant context) for a set of units, latest
   * end_date first — feeds active-contract + vacant-since resolution in the
   * property-drawer Units tab. Company-scoped + soft-delete filter. NEVER
   * throws: the pre-consolidation site destructured `{ data }` only (a failed
   * read renders units without contract context), so an error resolves to [].
   */
  async listUnitHistoryByUnitIds(companyId: string, unitIds: string[]) {
    const { data } = await supabase
      .from("contracts")
      .select(`
          id,
          unit_id,
          start_date,
          end_date,
          rent_amount,
          total_amount,
          status,
          tenant:tenants!contracts_tenant_id_fkey(
            id,
            first_name,
            last_name,
            commercial_name,
            tenant_type
          )
        `)
      .eq("company_id", companyId)
      .in("unit_id", unitIds)
      .is("deleted_at", null)
      .order("end_date", { ascending: false });
    return data ?? [];
  },

  /**
   * A property's active contracts overlapping `today` (rent columns only) —
   * the monthly-income figure. Company-scoped + soft-delete filter. KEPT on
   * Supabase.
   */
  async listActiveForMonthlyIncome(
    companyId: string,
    propertyId: string,
    today: string,
  ): Promise<
    {
      rent_amount: number | null;
      total_amount: number | null;
      start_date: string;
      end_date: string;
      payment_frequency: string | null;
    }[]
  > {
    const { data, error } = await supabase
      .from("contracts")
      .select("rent_amount, total_amount, start_date, end_date, payment_frequency")
      .eq("company_id", companyId)
      .eq("property_id", propertyId)
      .eq("status", "active")
      .is("deleted_at", null)
      .lte("start_date", today)
      .gte("end_date", today);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * One page of active-contract date windows (occupancy-report tenancy
   * durations) — the caller loops `range` pages of its chunk size. Company-
   * scoped + soft-delete filter. KEPT on Supabase.
   */
  async pageActiveDurations(
    companyId: string,
    from: number,
    to: number,
  ): Promise<{ start_date: string; end_date: string; status: string }[]> {
    const { data, error } = await supabase
      .from("contracts")
      .select("start_date, end_date, status")
      .eq("company_id", companyId)
      .eq("status", "active")
      .is("deleted_at", null)
      .range(from, to);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Active-contract count for the reports dashboard tile. Company-scoped +
   * soft-delete filter. NEVER throws: the pre-consolidation site read
   * `res.count` without an error check (tile falls back to 0), so an error
   * resolves to null.
   */
  async countActive(companyId: string): Promise<number | null> {
    const { count } = await supabase
      .from("contracts")
      .select("id", { count: "exact" })
      .eq("company_id", companyId)
      .eq("status", "active")
      .is("deleted_at", null);
    return count ?? null;
  },

  /**
   * Contracts ending on/before `beforeDate` (active, non-deleted), soonest
   * first — the dashboard activity feed's expiring-contracts card. Company-
   * scoped. KEPT on Supabase.
   */
  async listExpiringSoon(companyId: string, beforeDate: string, limit: number) {
    const { data, error } = await supabase
      .from("contracts")
      .select("id, contract_number, end_date, tenant:tenants(first_name, last_name)")
      .eq("company_id", companyId)
      .eq("status", "active")
      .is("deleted_at", null)
      .lte("end_date", beforeDate)
      .order("end_date", { ascending: true })
      .limit(limit);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Per-tenant contract rows (status, start, unit) for the tenants report's
   * contract-count / latest-contract maps. Company-scoped + soft-delete
   * filter, non-null tenants only. KEPT on Supabase.
   */
  async listForTenantsReport(companyId: string) {
    const { data, error } = await supabase
      .from("contracts")
      .select("id, tenant_id, status, start_date, unit:units(unit_number)")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .not("tenant_id", "is", null);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Non-deleted contract ids for one tenant (the tenant-financials /
   * tenant-invoices contract set). Company-scoped. NEVER throws: the
   * pre-consolidation sites destructured `{ data }` only (an empty set
   * renders zeroed financials), so an error resolves to [].
   */
  async listIdsByTenant(
    tenantId: string,
    companyId: string,
  ): Promise<{ id: string }[]> {
    const { data } = await supabase
      .from("contracts")
      .select("id")
      .eq("tenant_id", tenantId)
      .eq("company_id", companyId)
      .is("deleted_at", null);
    return data ?? [];
  },

  /**
   * `{ tenant_id }` of active contracts overlapping `today` (enhanced tenant
   * stats). Company-scoped + soft-delete filter. NEVER throws: the
   * pre-consolidation site destructured `{ data }` only (stats degrade to
   * zero active), so an error resolves to [].
   */
  async listActiveTenantIds(
    companyId: string,
    today: string,
  ): Promise<{ tenant_id: string | null }[]> {
    const { data } = await supabase
      .from("contracts")
      .select("tenant_id")
      .eq("company_id", companyId)
      .eq("status", "active")
      .is("deleted_at", null)
      .lte("start_date", today)
      .gte("end_date", today);
    return data ?? [];
  },

  /**
   * True when a contract with this EJAR number already exists for the
   * company (duplicate-import guard). Throws the wrapped check error
   * verbatim.
   */
  async existsByEjarNumber(
    companyId: string,
    ejarNumber: string,
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from("contracts")
      .select("id")
      .eq("company_id", companyId)
      .eq("ejar_number", ejarNumber)
      .maybeSingle();
    if (error) throw new Error(`EJAR duplicate check failed: ${error.message}`);
    return !!data;
  },

  /**
   * A unit's ACTIVE contracts (with tenant names), newest first — the
   * add-payment dialog's contract dropdown. Company-scoped + soft-delete
   * filter; throws on error.
   */
  async listActiveByUnit(unitId: string, companyId: string) {
    const { data, error } = await supabase
      .from("contracts")
      .select(`
          id,
          contract_number,
          status,
          tenant:tenants(first_name, last_name, commercial_name)
        `)
      .eq("unit_id", unitId)
      .eq("company_id", companyId)
      .eq("status", "active")
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  },

  /**
   * The contract_units junction rows for one contract (joined with basic
   * unit fields), position order — empty for legacy single-unit contracts.
   * Company-scoped; throws on error. (The table IS in the generated types
   * now — the pre-consolidation site's as-any cast died here.)
   */
  async listContractUnits(contractId: string, companyId: string) {
    const { data, error } = await supabase
      .from("contract_units")
      .select(
        `
          id,
          contract_id,
          unit_id,
          rent_amount,
          service_fees,
          position,
          unit:units(id, unit_number, floor_number, bedrooms, bathrooms, purpose)
        `
      )
      .eq("contract_id", contractId)
      .eq("company_id", companyId)
      .order("position", { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Contracts (with property/unit context) linked to any of a person's
   * owner-row fragments, newest first — the owner-details Contracts tab.
   * Company-scoped + soft-delete filter; throws on error. KEPT on Supabase.
   */
  async listByOwnerIds(companyId: string, ownerIds: string[]) {
    const { data, error } = await supabase
      .from("contracts")
      .select(
        `
          *,
          property:properties(name, name_en),
          unit:units(unit_number)
        `,
      )
      .eq("company_id", companyId)
      .in("owner_id", ownerIds)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  },

  /**
   * Super-admin global contract count — NOT company-scoped.
   *
   * Counts non-deleted contracts across ALL companies for the super-admin
   * dashboard KPIs, optionally narrowed by `status`. HEAD-only count query, so
   * zero rows are transferred. Documented cross-company exception to the
   * company-scoping used elsewhere in this repo: super_admin RLS spans every
   * company (the same path the super-admin dashboard already uses). Stays on
   * Supabase — no Nest flip (super-admin endpoints aren't built yet).
   */
  async countBySuperAdmin(status?: ContractStatus): Promise<number> {
    let query = supabase
      .from("contracts")
      .select("*", { count: "exact", head: true })
      .is("deleted_at", null);

    if (status) query = query.eq("status", status);

    const { count, error } = await query;
    if (error) {
      // Log with serializeError so empty-string messages don't produce the
      // opaque '{"message":""}' log that triggered Sentry issue #2271.
      console.error(
        `Error counting contracts (status=${status ?? "all"}):`,
        serializeError(error),
      );
      throw error;
    }
    return count ?? 0;
  },

  /**
   * Super-admin count of LAPSED active contracts — `status = 'active'` whose
   * `end_date` is already in the past. NOT company-scoped (see
   * `countBySuperAdmin`).
   *
   * The staff dashboard applies `getEffectiveStatus()` so a contract that is
   * still flagged `active` but whose term has ended displays as expired (the
   * status cron may not have run yet). The super-admin dashboard mirrors that by
   * subtracting this count from Active and adding it to Expired, instead of
   * trusting the raw `status` column. `todayDate` must be a `YYYY-MM-DD` string;
   * `< end_date` (strict) matches `getEffectiveStatus`'s `endDate < today`, so a
   * contract ending TODAY is still active, and NULL end_dates (open-ended) are
   * never treated as lapsed.
   */
  async countLapsedActiveForSuperAdmin(todayDate: string): Promise<number> {
    const { count, error } = await supabase
      .from("contracts")
      .select("*", { count: "exact", head: true })
      .is("deleted_at", null)
      .eq("status", "active")
      .lt("end_date", todayDate);
    if (error) {
      console.error(
        `Error counting lapsed contracts (today=${todayDate}):`,
        serializeError(error),
      );
      throw error;
    }
    return count ?? 0;
  },

  /**
   * Super-admin monthly-growth source — `created_at` of every non-deleted
   * contract since `sinceIso`, across ALL companies (NOT company-scoped; see
   * `countBySuperAdmin`). Selects only `created_at` so the payload is a single
   * column; the dashboard buckets these client-side into the 6-month chart.
   */
  async listRecentCreatedAtForSuperAdmin(sinceIso: string): Promise<{ created_at: string }[]> {
    const { data, error } = await supabase
      .from("contracts")
      .select("created_at")
      .is("deleted_at", null)
      .gte("created_at", sinceIso);

    if (error) {
      console.error("Error fetching recent contracts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { created_at: string }[];
  },

  /**
   * Which of these EJAR contract numbers already exist as live contracts in
   * the company (EjarImportDrawer duplicate pre-check — one batched query).
   * NEVER throws — the inline original destructured `{ data }` only (a failed
   * check just skips duplicate flagging).
   */
  async listExistingEjarNumbers(
    companyId: string,
    ejarNumbers: string[],
  ): Promise<{ ejar_number: string | null }[]> {
    const { data } = await supabase
      .from("contracts")
      .select("ejar_number")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .in("ejar_number", ejarNumbers);
    return data ?? [];
  },

  /**
   * Stamp a freshly-imported contract as Ejar-verified (optionally recording
   * the uploaded PDF's storage path). NEVER throws — the inline original
   * discarded the result entirely (the contract row is already created;
   * verification stamping is best-effort).
   */
  async markEjarVerified(
    contractId: string,
    ejarDocumentUrl: string | null,
    companyId: string,
  ): Promise<void> {
    await supabase
      .from("contracts")
      .update({
        ejar_verified: true,
        ejar_verified_at: new Date().toISOString(),
        ...(ejarDocumentUrl ? { ejar_document_url: ejarDocumentUrl } : {}),
      } as never)
      .eq("id", contractId)
      .eq("company_id", companyId);
  },

  /**
   * The unit's current active contract (end_date >= today, newest first,
   * limit 1) for the task property-context sidebar (fe-tail T9). NEVER
   * throws — the inline original destructured `{ data }` only, so an error
   * renders the card without the contract/tenant sections.
   */
  async taskContextActiveContracts(
    companyId: string,
    unitId: string,
  ): Promise<TaskContextContractRow[]> {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from("contracts")
      .select("id, rent_amount, start_date, end_date, status, tenant_id")
      .eq("company_id", companyId)
      .eq("unit_id", unitId)
      .eq("status", "active")
      .gte("end_date", today)
      .order("end_date", { ascending: false })
      .limit(1);
    return (data as TaskContextContractRow[] | null) ?? [];
  },
};
